/**
 * Hooks für den Kunden-Tab "Vertragsabgleich".
 *
 * Anlass: Vertrag WKR 0118 (Fortis Unum) - Position "vServer FreePBX" war
 * im Tool unsichtbar, und voip.fortis-unum.at lief unter Kunden itkfm.
 * Der Tab verknüpft Positionen mit Systemen, quittiert Entfallenes und
 * holt gehostete Maschinen zum Kunden.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  AcknowledgeRequest,
  AcknowledgeResponse,
  ContractReconciliation,
  LinkPositionRequest,
  LinkedSystem,
  MoveSystemRequest,
  MoveSystemResponse,
  UnlinkPositionRequest,
} from '@/types/contractReconciliation';

function invalidateAbgleich(qc: QueryClient, customerId: string): void {
  void qc.invalidateQueries({ queryKey: queryKeys.contracts.reconciliation(customerId) });
  void qc.invalidateQueries({ queryKey: queryKeys.contracts.changes });
  void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
}

export function useContractReconciliation(
  customerId: string | undefined
): ReturnType<typeof useQuery<ContractReconciliation>> {
  return useQuery<ContractReconciliation>({
    queryKey: queryKeys.contracts.reconciliation(customerId ?? ''),
    queryFn: () =>
      api.get<ContractReconciliation>(
        `/customers/${customerId ?? ''}/contract-reconciliation`
      ),
    enabled: Boolean(customerId),
  });
}

export interface LinkPositionInput {
  customerId: string;
  serviceProfileId: string;
  systemId: string;
  note?: string | null;
}

export function useLinkPosition(): ReturnType<
  typeof useMutation<LinkedSystem, Error, LinkPositionInput>
> {
  const qc = useQueryClient();
  return useMutation<LinkedSystem, Error, LinkPositionInput>({
    mutationFn: ({ customerId, serviceProfileId, systemId, note }) =>
      api.post<LinkedSystem, LinkPositionRequest>(
        `/customers/${customerId}/contract-reconciliation/link`,
        {
          service_profile_id: serviceProfileId,
          system_id: systemId,
          note: note ?? null,
        }
      ),
    onSuccess: (_res, { customerId }) => invalidateAbgleich(qc, customerId),
  });
}

export interface UnlinkPositionInput {
  customerId: string;
  serviceProfileId: string;
  systemId: string;
}

export function useUnlinkPosition(): ReturnType<
  typeof useMutation<void, Error, UnlinkPositionInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, UnlinkPositionInput>({
    mutationFn: ({ customerId, serviceProfileId, systemId }) =>
      api.post<void, UnlinkPositionRequest>(
        `/customers/${customerId}/contract-reconciliation/unlink`,
        { service_profile_id: serviceProfileId, system_id: systemId }
      ),
    onSuccess: (_res, { customerId }) => invalidateAbgleich(qc, customerId),
  });
}

export interface AcknowledgeInput {
  customerId: string;
  serviceProfileIds: string[];
}

export function useAcknowledgePositions(): ReturnType<
  typeof useMutation<AcknowledgeResponse, Error, AcknowledgeInput>
> {
  const qc = useQueryClient();
  return useMutation<AcknowledgeResponse, Error, AcknowledgeInput>({
    mutationFn: ({ customerId, serviceProfileIds }) =>
      api.post<AcknowledgeResponse, AcknowledgeRequest>(
        `/customers/${customerId}/contract-reconciliation/acknowledge`,
        { service_profile_ids: serviceProfileIds }
      ),
    onSuccess: (_res, { customerId }) => invalidateAbgleich(qc, customerId),
  });
}

export interface MoveSystemInput {
  systemId: string;
  /** Zielkunde. */
  customerId: string;
  /** Wer hostet künftig; null = Angabe löschen. */
  hostedByCustomerId?: string | null;
  /** Nur für die Cache-Invalidation: von wem das System kommt. */
  previousCustomerId?: string;
}

export function useMoveSystemToCustomer(): ReturnType<
  typeof useMutation<MoveSystemResponse, Error, MoveSystemInput>
> {
  const qc = useQueryClient();
  return useMutation<MoveSystemResponse, Error, MoveSystemInput>({
    mutationFn: ({ systemId, customerId, hostedByCustomerId }) =>
      api.post<MoveSystemResponse, MoveSystemRequest>(
        `/systems/${systemId}/move-to-customer`,
        {
          customer_id: customerId,
          hosted_by_customer_id: hostedByCustomerId ?? null,
        }
      ),
    onSuccess: (_res, { customerId, previousCustomerId }) => {
      invalidateAbgleich(qc, customerId);
      if (previousCustomerId) {
        void qc.invalidateQueries({
          queryKey: queryKeys.contracts.reconciliation(previousCustomerId),
        });
      }
    },
  });
}

export interface ArchiveSystemInput {
  customerId: string;
  systemId: string;
}

export function useArchiveSystem(): ReturnType<
  typeof useMutation<void, Error, ArchiveSystemInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, ArchiveSystemInput>({
    mutationFn: ({ systemId }) => api.delete<void>(`/systems/${systemId}`),
    onSuccess: (_res, { customerId }) => invalidateAbgleich(qc, customerId),
  });
}
